import { ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild, signal, ViewEncapsulation } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CompanyService } from '../../services/company.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CRMIntegrationsService } from '../../services/crm-integrations.service';
import { CommunicationService } from '../../services/communication.service';
import { TaggingService } from '../../services/tagging.service';
import { publicMethods } from '../../globals';
import { DashboardBulkSelection } from '../../types/dashboard-bulk-selection';
import { buildTradeTimeline, TradeTimeline } from './trade-timeline';
import { buildInsightCategoryChart } from './insight-category-chart';

declare var $: any;

@Component({
  selector: 'logistics-company-profile',
  templateUrl: './logistics-company-profile.component.html',
  styleUrl: './logistics-company-profile.component.css',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LogisticsCompanyProfileComponent {
  companyNumber = '';
  company: Record<string, any> = {};
  loading = true;
  loadingError = '';
  jobsListToRender: Record<string, any>[] = [];
  thirdPartyNewsSearchText = '';
  selfPublishedCompanyNewsSearchText = '';
  isSmallScreen = signal(window.innerWidth <= 700);
  faviconLoadFailed = false;
  userCustomProperties: Record<string, any> = {};
  workspaceFieldsLoading = true;
  workspaceFieldsUpdated = false;
  accountDetails: { email?: string; isSuperUser?: boolean } = {};
  teamMembers: Record<string, any>[] = [];
  availableCRMList: Record<string, any>[] = [];
  crmExporterName = '';
  companyActionSelection: DashboardBulkSelection = { mode: 'none' };
  readonly logisticsChartColours = ['#244a3b', '#4f7853', '#78984c', '#a5bd62', '#cedb8a', '#7a9f85', '#dbe9ac'];
  private tradeTimelineSource: any;
  private tradeTimelineCache: TradeTimeline;

  logisticsAddressWithCommas(address: string): string {
    // Company profile addresses arrive as newline-separated address fields.
    // Keep each field intact so street names and company names are not guessed from spaces.
    return String(address || '').split(/\r?\n/).map(part => part.trim().replace(/,\s*$/, '')).filter(Boolean).join(', ');
  }

  logisticsConfidencePercent(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const percent = Number(value);
    return Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : null;
  }

  logisticsConfidenceLabel(value: unknown): string {
    const percent = this.logisticsConfidencePercent(value);
    return percent === null ? 'Confidence unavailable' : `${Math.round(percent)}% confidence`;
  }

  logisticsConfidenceColour(value: unknown): string {
    const percent = this.logisticsConfidencePercent(value);
    if (percent === null) return '#8b9b90';

    // Continuous stops: red through 50%, warm yellow in the middle, green at 80%+.
    const stops = [
      { percent: 0, colour: [165, 54, 53] },
      { percent: 50, colour: [212, 80, 66] },
      { percent: 65, colour: [198, 158, 42] },
      { percent: 80, colour: [78, 153, 89] },
      { percent: 100, colour: [36, 124, 77] },
    ];
    const upperIndex = stops.findIndex(stop => percent <= stop.percent);
    const upper = stops[upperIndex];
    const lower = stops[Math.max(0, upperIndex - 1)];
    const mix = upper === lower ? 0 : (percent - lower.percent) / (upper.percent - lower.percent);
    const channels = upper.colour.map((channel, index) =>
      Math.round(lower.colour[index] + (channel - lower.colour[index]) * mix)
        .toString(16).padStart(2, '0')
    );
    return `#${channels.join('')}`;
  }

  get logisticsFaviconDomain(): string {
    const website = this.company?.company_overview?.domain || this.company?.company_overview?.url;
    if (!website) return '';
    try {
      return new URL(website.includes('://') ? website : `https://${website}`).hostname;
    } catch (_) {
      return '';
    }
  }

  get logisticsPhoneHref(): string {
    const phone = String(this.company?.company_overview?.phone_number || '').replace(/[^\d+]/g, '');
    return /\d/.test(phone) ? `tel:${phone}` : '';
  }

  get logisticsEmailHref(): string {
    const email = String(this.company?.company_overview?.email || '').trim();
    return /^[^\s@?&]+@[^\s@?&]+\.[^\s@?&]+$/.test(email) ? `mailto:${email}` : '';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private crmService: CRMIntegrationsService,
    private communicationService: CommunicationService,
    private taggingService: TaggingService,
    private authenticationService: AuthenticationService,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    public pubMethods: publicMethods,
  ) {}

  @HostListener('window:resize')
  onResize(): void {
    this.isSmallScreen.set(window.innerWidth <= 700);
  }

  ngOnInit(): void {
    this.companyNumber = this.route.snapshot.paramMap.get('id') || '';
    this.communicationService.getEmailMessage().subscribe(email => this.accountDetails.email = email);
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => this.accountDetails.isSuperUser = isSuperUser);
    try {
      const saved = window.localStorage.getItem('zint-logistics-results-perspective');
      if (this.logisticsLenses.some(lens => lens.id === saved)) this.logisticsLens = saved;
    } catch (_) {}
    this.loadLogisticsProfileInsights();
    this.loadWorkspaceFields();
    this.companyService.getCompanyProfileData(this.companyNumber).subscribe({
      next: data => {
        this.company = data?.company || {};
        const companyId = Number(this.company.company_id);
        this.companyActionSelection = Number.isSafeInteger(companyId) && companyId > 0
          ? { mode: 'ids', ids: [companyId], searchParams: new HttpParams() }
          : { mode: 'none' };
        this.jobsListToRender = this.company.hiring_for || [];
        this.titleService.setTitle(`${this.company.company_overview?.name || 'Company'} | Zint Logistics`);
        this.loading = false;
      },
      error: error => {
        this.loadingError = error?.error?.message || 'Company profile could not be loaded.';
        this.loading = false;
      },
    });
    this.crmService.getAvailableCRMs().subscribe(data => {
      this.availableCRMList = Array.isArray(data) ? data : [];
    });
  }

  loadWorkspaceFields(): void {
    this.workspaceFieldsLoading = true;
    this.companyService.getCompanyUserDetails(this.companyNumber).subscribe({
      next: data => {
        this.userCustomProperties = data || {};
        this.teamMembers = this.userCustomProperties.team_members || [];
        (this.userCustomProperties.custom_columns || []).forEach(column => {
          if (column.column_type === 'User') column.options = this.teamMembers;
        });
        this.workspaceFieldsLoading = false;
      },
      error: () => { this.workspaceFieldsLoading = false; },
    });
  }

  get logisticsWorkspaceColumns(): Record<string, any>[] {
    return (this.userCustomProperties.custom_columns || []).filter(column =>
      !column.is_propensity || (column.value !== null && column.value !== undefined && column.value !== '')
    );
  }

  get logisticsWorkspaceFilledCount(): number {
    return this.logisticsWorkspaceColumns.filter(column =>
      column.value !== null && column.value !== undefined && column.value !== ''
    ).length;
  }

  get logisticsWorkspaceTags(): Record<string, any>[] {
    return [
      ...(this.userCustomProperties.tags || []).map(tag => ({ ...tag, can_remove: true })),
      ...(this.userCustomProperties.team_member_tags || []).map(tag => ({ ...tag, can_remove: false })),
      ...(this.userCustomProperties.organisation_tags || []).map(tag => ({ ...tag, can_remove: !!tag.can_edit })),
    ].filter(tag => !tag.is_propensity);
  }

  removeWorkspaceTag(tag: Record<string, any>): void {
    if (!tag.can_remove) return;
    this.taggingService.tagOrUntagCompanies(tag.tag_id, [this.company.company_id], null, false).subscribe({
      next: data => {
        if (data?.total === 1) this.loadWorkspaceFields();
        else this.pubMethods.showInfoMessage('Could not remove tag.');
      },
      error: () => this.pubMethods.showInfoMessage('Could not remove tag.'),
    });
  }

  onWorkspaceFieldSaved(value: any): void {
    this.workspaceFieldsUpdated = true;
    this.loadWorkspaceFields();
  }

  get hasLogisticsHeatScore(): boolean {
    return this.pubMethods.hasData(this.userCustomProperties?.heat_score);
  }

  openLogisticsCompanyAction(action: 'Tag' | 'CRM', crmName = ''): void {
    if (this.companyActionSelection.mode === 'none') return;
    if (action === 'CRM') {
      this.crmExporterName = crmName;
      this.cdr.detectChanges();
    }
    $(action === 'Tag' ? '#tagModal' : '#exportToCRMModal').modal();
  }

  logisticsCRMIcon(crmType: string): string {
    if (crmType === 'hubspot' || crmType === 'salesforce') {
      return `<i class="fab fa-${crmType}"></i>`;
    }
    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  logisticsInsightModules: any[] = [];
  logisticsInsightsLoading = false;
  logisticsInsightsError = false;
  readonly logisticsInitialCardCount = 6;
  showAllLogisticsInsights = false;
  logisticsProductsExpanded = false;
  logisticsExpandedLists = new Set<string>();

  logisticsInsightListKey(insight: any): string {
    return `${insight.module}:${insight.key}`;
  }

  toggleLogisticsInsightList(insight: any): void {
    const key = this.logisticsInsightListKey(insight);
    if (this.logisticsExpandedLists.has(key)) this.logisticsExpandedLists.delete(key);
    else this.logisticsExpandedLists.add(key);
  }

  logisticsInsightListItems(insight: any): any[] {
    return Array.isArray(insight.value) ? insight.value : [];
  }

  logisticsInsightListText(item: any, schema: any): string {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (!item || typeof item !== 'object') return '';
    const key = schema?.text_property_name || schema?.header_key_name;
    return String((key && item[key]) || Object.values(item).find(value => typeof value === 'string') || '');
  }

  logisticsInsightListDetail(item: any, schema: any): string {
    if (!item || typeof item !== 'object') return '';
    const key = schema?.content_key_name || schema?.on_hover_property_name;
    const detail = key && item[key];
    return detail && detail !== this.logisticsInsightListText(item, schema) ? String(detail) : '';
  }

  get logisticsProductsAndServices(): string[] {
    const overview = this.company?.company_overview;
    const products = overview?.products_services?.length ? overview.products_services : overview?.keywords;
    return Array.isArray(products) ? products.filter((item): item is string => typeof item === 'string' && !!item.trim()) : [];
  }

  logisticsProductName(item: string): string {
    return item.split(' - ').pop() || item;
  }

  selectedLogisticsInsight: any = null;
  private logisticsEvidenceTrigger: HTMLElement | null = null;
  @ViewChild('logisticsEvidenceClose') logisticsEvidenceClose: ElementRef<HTMLButtonElement>;

  openLogisticsEvidence(insight: any): void {
    this.logisticsEvidenceTrigger = document.activeElement as HTMLElement;
    this.selectedLogisticsInsight = insight;
    setTimeout(() => this.logisticsEvidenceClose?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape')
  closeLogisticsEvidence(): void {
    if (!this.selectedLogisticsInsight) return;
    this.selectedLogisticsInsight = null;
    setTimeout(() => this.logisticsEvidenceTrigger?.focus());
  }
  logisticsLens = 'overview';
  readonly logisticsLenses = [
    { id: 'overview', label: 'Overview', icon: 'fa-grid-2', description: 'The whole opportunity' },
    { id: 'freight-forwarding', label: 'Forwarding', icon: 'fa-truck-fast', description: 'Freight and flows' },
    { id: 'third-party-logistics', label: '3PL', icon: 'fa-warehouse', description: 'Storage and handling' },
    { id: 'parcel-courier', label: 'Courier', icon: 'fa-box-taped', description: 'Parcel demand' },
    { id: 'customs-trade', label: 'Customs', icon: 'fa-globe-europe', description: 'Cross-border trade' },
  ];
  setLogisticsLens(id: string): void {
    this.logisticsLens = id;
    this.showAllLogisticsInsights = false;
    try { window.localStorage.setItem('zint-logistics-results-perspective', id); } catch (_) {}
  }

  get logisticsInsightCards(): any[] {
    const cards = this.logisticsInsightModules.flatMap(module =>
      (module.responses || []).flatMap(response => {
        const properties = response.display_schema?.properties;
        const entries = properties
          ? Object.entries(properties).map(([key, schema]) => ({ key, schema, value: response.answer?.[key] }))
          : [{ key: response.display_schema?.label || response.question || '', schema: response.display_schema, value: response.answer }];
        return entries.filter(entry => this.hasLogisticsInsightValue(entry.value)).map(entry => {
          const categoryChart = buildInsightCategoryChart(entry.schema, entry.value);
          return {
            ...entry,
            categoryChart,
            displayType: categoryChart ? 'pie_chart' : (entry.schema as any)?.display_type,
            response,
            module: module.module,
            title: (entry.schema as any)?.label || entry.key.replace(/_/g, ' '),
            section: this.logisticsInsightSection(entry.key + ' ' + ((entry.schema as any)?.label || '')),
          };
        });
      })
    );
    const lensSections: Record<string, string[]> = {
      overview: ['parcel', 'freight', 'general', 'international', 'warehouse'],
      'freight-forwarding': ['freight', 'international', 'general', 'warehouse', 'parcel'],
      'third-party-logistics': ['warehouse', 'freight', 'general', 'parcel', 'international'],
      'parcel-courier': ['parcel', 'general', 'international', 'warehouse', 'freight'],
      'customs-trade': ['international', 'freight', 'general', 'parcel', 'warehouse'],
    };
    const order = lensSections[this.logisticsLens] || lensSections.overview;
    const isSalesCopy = (card: any) => /email_opener|identified_hooks/.test(card.key);
    // Lead with the answer formats that make the operational mix legible at a glance.
    // Keep the answer and its display schema together so the existing Magic Insights
    // chart/list components can render them without changing the evidence dialog.
    const visualPriority = (card: any): number => ({
      vertical_bar_chart: 0, pie_chart: 0, gauge: 0,
      card_list: 1, string_list: 1, labelled_pills: 2, pills: 2,
      number: 3, string: 4,
    })[card.displayType] ?? 4;
    if (this.logisticsLens === 'overview') {
      // Give the overview a mix of signals before any one category fills the page.
      const buckets = order.map(section => cards.filter(card => card.section === section && !isSalesCopy(card))
        .sort((a, b) => visualPriority(a) - visualPriority(b)));
      const overview = [];
      while (buckets.some(bucket => bucket.length)) {
        for (const bucket of buckets) {
          if (bucket.length) overview.push(bucket.shift());
        }
      }
      return [...overview, ...cards.filter(isSalesCopy)];
    }
    return cards.sort((a, b) =>
      Number(isSalesCopy(a)) - Number(isSalesCopy(b)) ||
      order.indexOf(a.section) - order.indexOf(b.section) ||
      visualPriority(a) - visualPriority(b)
    );
  }

  get visibleLogisticsInsightCards(): any[] {
    const cards = this.logisticsInsightCards;
    return this.showAllLogisticsInsights ? cards : cards.slice(0, this.logisticsInitialCardCount);
  }

  get logisticsTradeActivity() {
    const source = this.company['imports_&_exports'];
    if (source !== this.tradeTimelineSource || !this.tradeTimelineCache) {
      this.tradeTimelineSource = source;
      this.tradeTimelineCache = buildTradeTimeline(source);
    }
    return this.tradeTimelineCache;
  }

  hasLogisticsInsightValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '' &&
      (!Array.isArray(value) || value.length > 0) &&
      (typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length > 0);
  }

  logisticsInsightSection(text: string): string {
    const value = text.toLowerCase();
    if (/email.opener|identified.hooks/.test(value)) return 'general';
    if (/warehouse|storage|fulfil|pallet|wms/.test(value)) return 'warehouse';
    if (/parcel|return|marketplace|b2c|ecommerce/.test(value)) return 'parcel';
    if (/country|countr|international|geograph|container|customs|cross.border/.test(value)) return 'international';
    if (/freight|transport|shipment|shipping|carrier|mode|vehicle/.test(value)) return 'freight';
    return 'general';
  }

  logisticsInsightValue(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(value);
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.slice(0, 3).map(item => this.logisticsInsightValue(item)).join(' · ');
    const fields = Object.entries(value).filter(([key, item]) =>
      key !== 'domain' && key !== 'country_code' && this.hasLogisticsInsightValue(item)
    );
    return fields.slice(0, 2).map(([, item]) => this.logisticsInsightValue(item)).join(' · ') || 'View detail';
  }

  logisticsInsightDetailRows(value: any, label: string): Array<{ label: string; value: string }> {
    const rows: Array<{ label: string; value: string }> = [];
    const visit = (item: any, path: string): void => {
      if (item === null || item === undefined) {
        rows.push({ label: path, value: 'Not available' });
      } else if (Array.isArray(item)) {
        if (!item.length) rows.push({ label: path, value: 'None supplied' });
        item.forEach((entry, index) => visit(entry, `${path} ${index + 1}`));
      } else if (typeof item === 'object') {
        const fields = Object.entries(item);
        if (!fields.length) rows.push({ label: path, value: 'No details supplied' });
        fields.forEach(([key, entry]) => visit(entry, `${path} · ${key.replace(/_/g, ' ')}`));
      } else {
        rows.push({ label: path, value: this.logisticsInsightValue(item) });
      }
    };
    visit(value, label);
    return rows;
  }

  loadLogisticsProfileInsights(): void {
    if (!this.companyNumber) return;
    this.logisticsInsightsLoading = true;
    this.logisticsInsightsError = false;
    this.companyService.getLogisticsProfileInsights(this.companyNumber).subscribe({
      next: modules => {
        this.logisticsInsightModules = Array.isArray(modules) ? modules : [];
        this.logisticsInsightsLoading = false;
      },
      error: () => {
        this.logisticsInsightsError = true;
        this.logisticsInsightsLoading = false;
      },
    });
  }

  switchProfileMode(mode: 'full' | 'logistics'): void {
    this.authenticationService.setDefaultSkin(mode).subscribe({
      next: () => this.router.navigate(
        mode === 'logistics'
          ? ['/profile', this.companyNumber, 'logistics']
          : ['/profile', this.companyNumber],
        {
          queryParams: this.route.snapshot.queryParams,
          fragment: this.route.snapshot.fragment,
        }
      ),
      error: () => this.pubMethods.showInfoMessage('Could not save your profile preference. Please try again.'),
    });
  }
}
